/**
 * Pest Modal Component
 * Modal for creating and editing pests with action plans and materials
 */

import React, { useState, useEffect } from "react";
import { Icon } from "@/components/ui";
import { AlertModal } from "@/components/ui/AlertModal/AlertModal";
import { materialsService } from "@/services/materialsService";
import type { APIMaterial } from "@/types";
import styles from "./PestModal.module.css";

interface ActionStep {
  id: string;
  order: number;
  description: string;
}

interface Material {
  id: string;
  name: string;
  type:
    | "Producto Químico"
    | "Herramienta"
    | "Equipo de Protección"
    | "Material General";
  quantity: number;
  required: boolean;
}

interface InventoryItem {
  id: string;
  name: string;
  category: string;
  quantity: number;
  unit: string;
}

const mapMaterialCategoryToInventory = (category?: string): string => {
  switch (category) {
    case "insecticides":
      return "Insectos";
    case "rodenticides":
      return "Roedores";
    case "traps":
      return "Trampas";
    case "equipment":
      return "Herramientas";
    case "disinfectants":
      return "Seguridad";
    default:
      return "Material General";
  }
};

const mapAPIMaterialToInventory = (material: APIMaterial): InventoryItem => ({
  id: String(material.id),
  name: material.name,
  category: mapMaterialCategoryToInventory(material.category),
  quantity: material.quantity ?? 0,
  unit: material.unit ?? "unidades",
});

interface Pest {
  id?: string;
  name: string;
  category: "Insectos" | "Roedores" | "Aves" | "Legionela" | "Otros";
  icon?: string;
  actionPlan: ActionStep[];
  materials: Material[];
  preventions: string[];
  is_recurrent?: boolean;
  recurrence_period?: "week" | "month" | "year" | null;
}

interface PestModalProps {
  isOpen: boolean;
  pest: Pest | null;
  onClose: () => void;
  onSave: (pest: Pest) => void;
}

export function PestModal({ isOpen, pest, onClose, onSave }: PestModalProps) {
  const [activeTab, setActiveTab] = useState<
    "general" | "action" | "materials"
  >("general");
  const [materialFilter, setMaterialFilter] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);

  // Inicializar formData basado en pest prop (solo cuando cambia)
  const getInitialData = (): Pest => {
    if (pest) {
      return pest;
    }
    return {
      name: "",
      category: "Insectos",
      icon: "🐛",
      actionPlan: [],
      materials: [],
      preventions: [""],
      is_recurrent: false,
      recurrence_period: null,
    };
  };

  const [formData, setFormData] = useState<Pest>(getInitialData);
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    message: string;
    type: "info" | "warning" | "error" | "success";
  }>({
    isOpen: false,
    message: "",
    type: "info",
  });

  useEffect(() => {
    if (isOpen) {
      setActiveTab("general");
      setFormData(getInitialData());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const loadInventory = async () => {
      try {
        const response = await materialsService.list({
          page: 1,
          page_size: 500,
        });
        setInventoryItems(response.results.map(mapAPIMaterialToInventory));
      } catch {
        setInventoryItems([]);
      }
    };

    void loadInventory();
  }, [isOpen]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Validaciones
    if (!formData.name.trim()) {
      setAlertModal({
        isOpen: true,
        message: "El nombre de la plaga es obligatorio",
        type: "warning",
      });
      return;
    }

    // Filtrar prevenciones vacías
    const cleanedData = {
      ...formData,
      preventions: formData.preventions.filter((p) => p.trim() !== ""),
    };

    onSave(cleanedData);
  };

  // Preventions handlers
  const addPrevention = () => {
    setFormData({ ...formData, preventions: [...formData.preventions, ""] });
  };

  const updatePrevention = (index: number, value: string) => {
    const newPreventions = [...formData.preventions];
    newPreventions[index] = value;
    setFormData({ ...formData, preventions: newPreventions });
  };

  const removePrevention = (index: number) => {
    if (formData.preventions.length <= 1) return;
    setFormData({
      ...formData,
      preventions: formData.preventions.filter((_, i) => i !== index),
    });
  };

  // Action Plan handlers
  const addActionStep = () => {
    const newStep: ActionStep = {
      id: String(Date.now()),
      order: formData.actionPlan.length + 1,
      description: "",
    };
    setFormData({ ...formData, actionPlan: [...formData.actionPlan, newStep] });
  };

  const updateActionStep = (
    index: number,
    field: keyof ActionStep,
    value: string | boolean,
  ) => {
    const newPlan = [...formData.actionPlan];
    newPlan[index] = { ...newPlan[index], [field]: value };
    setFormData({ ...formData, actionPlan: newPlan });
  };

  const removeActionStep = (index: number) => {
    if (confirm("¿Estás seguro de eliminar este paso?")) {
      const newPlan = formData.actionPlan
        .filter((_, i) => i !== index)
        .map((step, i) => ({ ...step, order: i + 1 }));
      setFormData({ ...formData, actionPlan: newPlan });
    }
  };

  const handleDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", index.toString());
    setDraggedIndex(index);
  };

  const handleDragOver = (
    e: React.DragEvent<HTMLDivElement>,
    index: number,
  ) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";

    if (draggedIndex === null || draggedIndex === index) return;

    // Reorganizar visualmente en tiempo real
    if (dragOverIndex !== index) {
      const newPlan = [...formData.actionPlan];
      const draggedItem = newPlan[draggedIndex];

      // Remover el elemento de su posición actual
      newPlan.splice(draggedIndex, 1);
      // Insertarlo en la nueva posición
      newPlan.splice(index, 0, draggedItem);

      // Actualizar números de orden
      newPlan.forEach((step, i) => {
        step.order = i + 1;
      });

      setFormData({ ...formData, actionPlan: newPlan });
      setDraggedIndex(index); // Actualizar el índice del elemento arrastrado
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // Ya está reorganizado por handleDragOver, solo limpiamos estados
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // Materials handlers
  const addMaterialFromInventory = (inventoryItem: InventoryItem) => {
    // Mapear la categoría del inventario al tipo de material
    const typeMap: Record<string, Material["type"]> = {
      Insectos: "Producto Químico",
      Roedores: "Producto Químico",
      Termitas: "Producto Químico",
      Trampas: "Material General",
      Herramientas: "Herramienta",
      Seguridad: "Equipo de Protección",
    };

    const newMaterial: Material = {
      id: inventoryItem.id,
      name: inventoryItem.name,
      type: typeMap[inventoryItem.category] || "Material General",
      quantity: 1,
      required: true,
    };

    setFormData({
      ...formData,
      materials: [...formData.materials, newMaterial],
    });
    setMaterialFilter(""); // Limpiar el filtro después de agregar
  };

  const getFilteredInventory = () => {
    return inventoryItems.filter((item) => {
      const matchesSearch = item.name
        .toLowerCase()
        .includes(materialFilter.toLowerCase());
      const matchesCategory =
        selectedCategory === "all" || item.category === selectedCategory;
      const notAlreadyAdded = !formData.materials.some(
        (m) => m.name === item.name,
      );
      return matchesSearch && matchesCategory && notAlreadyAdded;
    });
  };

  const getUniqueCategories = () => {
    const categories = new Set(inventoryItems.map((item) => item.category));
    return Array.from(categories);
  };

  const updateMaterial = (
    index: number,
    field: keyof Material,
    value: string | boolean,
  ) => {
    const newMaterials = [...formData.materials];
    if (field === "quantity") {
      newMaterials[index] = {
        ...newMaterials[index],
        quantity: Number(value) || 0,
      };
    } else {
      newMaterials[index] = { ...newMaterials[index], [field]: value };
    }
    setFormData({ ...formData, materials: newMaterials });
  };

  const removeMaterial = (index: number) => {
    if (confirm("¿Estás seguro de eliminar este material?")) {
      setFormData({
        ...formData,
        materials: formData.materials.filter((_, i) => i !== index),
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h2>{pest ? "Editar Plaga" : "Nueva Plaga"}</h2>
          <button className={styles.closeButton} onClick={onClose}>
            <Icon name="x" size={24} />
          </button>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === "general" ? styles.active : ""}`}
            onClick={() => setActiveTab("general")}
          >
            <Icon name="info" size={18} />
            General
          </button>
          <button
            className={`${styles.tab} ${activeTab === "action" ? styles.active : ""}`}
            onClick={() => setActiveTab("action")}
          >
            <Icon name="list" size={18} />
            Plan de Actuación
          </button>
          <button
            className={`${styles.tab} ${activeTab === "materials" ? styles.active : ""}`}
            onClick={() => setActiveTab("materials")}
          >
            <Icon name="package" size={18} />
            Materiales
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          {/* General Tab */}
          {activeTab === "general" && (
            <div className={styles.tabContent}>
              <div className={styles.formGroup}>
                <label htmlFor="name">Nombre de la Plaga *</label>
                <div className={styles.nameWithIcon}>
                  <div className={styles.iconPickerWrapper}>
                    <button
                      type="button"
                      className={styles.iconPickerButton}
                      onClick={() => setIconPickerOpen(!iconPickerOpen)}
                    >
                      <span className={styles.selectedIcon}>
                        {formData.icon || "🐛"}
                      </span>
                    </button>
                    {iconPickerOpen && (
                      <div className={styles.iconPickerPopup}>
                        {[
                          "🐛",
                          "🪳",
                          "🦟",
                          "🪰",
                          "🐀",
                          "🐁",
                          "🦇",
                          "🕷️",
                          "🦂",
                          "🐜",
                          "🦗",
                          "🪱",
                          "🕊️",
                          "🦅",
                          "🦠",
                        ].map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            className={`${styles.iconButton} ${formData.icon === emoji ? styles.iconSelected : ""}`}
                            onClick={() => {
                              setFormData({ ...formData, icon: emoji });
                              setIconPickerOpen(false);
                            }}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                    placeholder="Ej: Cucarachas"
                  />
                </div>
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="category">Categoría *</label>
                <select
                  id="category"
                  value={formData.category}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      category: e.target.value as Pest["category"],
                    })
                  }
                  required
                >
                  <option value="Insectos">Insectos</option>
                  <option value="Roedores">Roedores</option>
                  <option value="Aves">Aves</option>
                  <option value="Legionela">Legionela</option>
                  <option value="Otros">Otros</option>
                </select>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkbox}>
                  <input
                    type="checkbox"
                    checked={formData.is_recurrent ?? false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        is_recurrent: e.target.checked,
                        recurrence_period: e.target.checked
                          ? (formData.recurrence_period ?? "month")
                          : null,
                      })
                    }
                  />
                  Plaga recurrente
                </label>
              </div>

              {formData.is_recurrent && (
                <div className={styles.formGroup}>
                  <label htmlFor="recurrencePeriod">
                    Periodo de recurrencia
                  </label>
                  <select
                    id="recurrencePeriod"
                    value={formData.recurrence_period ?? "month"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        recurrence_period: e.target.value as
                          | "week"
                          | "month"
                          | "year",
                      })
                    }
                  >
                    <option value="week">Cada semana</option>
                    <option value="month">Cada mes</option>
                    <option value="year">Cada año</option>
                  </select>
                </div>
              )}

              <div className={styles.formGroup}>
                <label>Medidas Preventivas</label>
                {formData.preventions.map((prevention, index) => (
                  <div key={index} className={styles.listItem}>
                    <input
                      type="text"
                      value={prevention}
                      onChange={(e) => updatePrevention(index, e.target.value)}
                      placeholder="Medida preventiva..."
                    />
                    {formData.preventions.length > 1 && (
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => removePrevention(index)}
                      >
                        <Icon name="x" size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  className={styles.addButton}
                  onClick={addPrevention}
                >
                  <Icon name="plus" size={16} />
                  Añadir medida
                </button>
              </div>
            </div>
          )}

          {/* Action Plan Tab */}
          {activeTab === "action" && (
            <div className={styles.tabContent}>
              <div className={styles.sectionHeader}>
                <p>Define los pasos del plan de actuación en orden</p>
                <button
                  type="button"
                  className={styles.addButton}
                  onClick={addActionStep}
                >
                  <Icon name="plus" size={16} />
                  Añadir paso
                </button>
              </div>

              {formData.actionPlan.length === 0 ? (
                <div className={styles.emptyState}>
                  <Icon name="list" size={48} />
                  <p>
                    No hay pasos definidos. Añade el primer paso del plan de
                    actuación.
                  </p>
                </div>
              ) : (
                <div className={styles.actionPlanList}>
                  {formData.actionPlan.map((step, index) => (
                    <div
                      key={step.id}
                      className={`${styles.actionStepEdit} ${
                        draggedIndex === index ? styles.dragging : ""
                      } ${dragOverIndex === index ? styles.dragOver : ""}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDragEnd={handleDragEnd}
                      onDrop={handleDrop}
                    >
                      <div className={styles.stepOrder}>
                        <div
                          className={styles.dragHandle}
                          title="Arrastra para reordenar"
                        >
                          <Icon name="menu" size={20} />
                        </div>
                        <span className={styles.stepNumber}>{step.order}</span>
                      </div>

                      <div className={styles.stepFields}>
                        <input
                          type="text"
                          value={step.description}
                          onChange={(e) =>
                            updateActionStep(
                              index,
                              "description",
                              e.target.value,
                            )
                          }
                          placeholder="Descripción del paso..."
                          required
                          className={styles.stepDescription}
                        />
                      </div>

                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => removeActionStep(index)}
                      >
                        <Icon name="x" size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Materials Tab */}
          {activeTab === "materials" && (
            <div className={styles.tabContent}>
              <div className={styles.sectionHeader}>
                <p>Selecciona materiales de tu inventario</p>
              </div>

              {/* Filtros */}
              <div className={styles.inventoryFilters}>
                <input
                  type="text"
                  placeholder="Buscar material por nombre..."
                  value={materialFilter}
                  onChange={(e) => setMaterialFilter(e.target.value)}
                  className={styles.searchInput}
                />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className={styles.categoryFilter}
                >
                  <option value="all">Todas las categorías</option>
                  {getUniqueCategories().map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>

              {/* Lista de inventario disponible */}
              {materialFilter || selectedCategory !== "all" ? (
                <div className={styles.inventoryList}>
                  {getFilteredInventory().length > 0 ? (
                    getFilteredInventory().map((item) => (
                      <div
                        key={item.id}
                        className={styles.inventoryItem}
                        onClick={() => addMaterialFromInventory(item)}
                      >
                        <div className={styles.inventoryItemInfo}>
                          <span className={styles.inventoryItemName}>
                            {item.name}
                          </span>
                          <span className={styles.inventoryItemCategory}>
                            {item.category}
                          </span>
                        </div>
                        <div className={styles.inventoryItemStock}>
                          <span>
                            {item.quantity} {item.unit}
                          </span>
                          <Icon name="plus" size={16} />
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className={styles.emptyState}>
                      <Icon name="search" size={48} />
                      <p>No se encontraron materiales con ese criterio</p>
                    </div>
                  )}
                </div>
              ) : (
                <div className={styles.emptyState}>
                  <Icon name="search" size={48} />
                  <p>Usa el buscador o filtro para encontrar materiales</p>
                </div>
              )}

              {/* Materiales seleccionados */}
              <div className={styles.selectedMaterialsSection}>
                <h4>Materiales seleccionados ({formData.materials.length})</h4>
                {formData.materials.length === 0 ? (
                  <div className={styles.emptyState}>
                    <Icon name="package" size={48} />
                    <p>No hay materiales seleccionados</p>
                  </div>
                ) : (
                  <div className={styles.materialsList}>
                    {formData.materials.map((material, index) => (
                      <div key={material.id} className={styles.materialEdit}>
                        <div className={styles.materialInfo}>
                          <span className={styles.materialName}>
                            {material.name}
                          </span>
                          <span className={styles.materialType}>
                            {material.type}
                          </span>
                        </div>
                        <div className={styles.materialActions}>
                          <input
                            type="number"
                            min="1"
                            value={material.quantity}
                            onChange={(e) =>
                              updateMaterial(
                                index,
                                "quantity",
                                // @ts-expect-error: quantity expects string but number is valid here
                                parseInt(e.target.value) || 1,
                              )
                            }
                            placeholder="Cantidad"
                            className={styles.quantityInput}
                          />
                          <button
                            type="button"
                            className={styles.removeButton}
                            onClick={() => removeMaterial(index)}
                            title="Eliminar material"
                          >
                            <Icon name="x" size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Modal Footer */}
          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={onClose}
            >
              Cancelar
            </button>
            <button type="submit" className={styles.saveButton}>
              <Icon name="checkCircle" size={18} />
              {pest ? "Guardar Cambios" : "Crear Plaga"}
            </button>
          </div>
        </form>
      </div>

      {/* AlertModal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
        message={alertModal.message}
        type={alertModal.type}
      />
    </div>
  );
}
